import { useEffect, useState } from "react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { SlidersHorizontal, X, CalendarDays } from "lucide-react";
import { CATEGORIES, SOURCES, LEAD_TYPES, ROLES } from "@/lib/constants";
import api from "@/lib/api";

const ANY = "__any__";

/**
 * Reusable filter bar used in Dashboard, Leads, Reports, Analytics.
 * `enabled` selects which filters to show.
 * `value` is the controlled filter state object.
 */
export function FilterBar({ value, onChange, enabled = ["q", "category", "source", "type"], compact = false }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (enabled.includes("user") || enabled.includes("created_by") || enabled.includes("owner_id")) {
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [enabled.join(",")]);

  const set = (k) => (v) => onChange({ ...value, [k]: v === ANY ? "" : v });
  const setText = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  const has = Object.entries(value || {}).some(([, v]) => v !== "" && v != null);

  return (
    <div className={`flex flex-wrap gap-2 items-center ${compact ? "" : "p-3 border border-border rounded-sm bg-background"}`}>
      {enabled.includes("q") && (
        <input
          value={value.q || ""}
          onChange={setText("q")}
          placeholder="Search…"
          data-testid="filter-q"
          className="flex-1 min-w-[200px] bg-transparent border border-border rounded-sm px-3 py-1.5 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
        />
      )}

      {enabled.includes("category") && (
        <FBSelect label="Category" testId="filter-category"
          value={value.category} onChange={set("category")} options={CATEGORIES} />
      )}
      {enabled.includes("source") && (
        <FBSelect label="Source" testId="filter-source"
          value={value.source} onChange={set("source")} options={SOURCES} />
      )}
      {enabled.includes("type") && (
        <FBSelect label="Type" testId="filter-type"
          value={value.type} onChange={set("type")} options={LEAD_TYPES} />
      )}
      {enabled.includes("role") && (
        <FBSelect label="Role" testId="filter-role"
          value={value.role} onChange={set("role")}
          options={ROLES.map((r) => r.value)} optionLabels={Object.fromEntries(ROLES.map((r) => [r.value, r.label]))} />
      )}
      {enabled.includes("user") && (
        <FBSelect label="User" testId="filter-user"
          value={value.user_id} onChange={set("user_id")}
          options={users.map((u) => u.id)}
          optionLabels={Object.fromEntries(users.map((u) => [u.id, u.full_name]))} />
      )}
      {enabled.includes("owner_id") && (
        <FBSelect label="Assigned to" testId="filter-owner"
          value={value.owner_id} onChange={set("owner_id")}
          options={users.map((u) => u.id)}
          optionLabels={Object.fromEntries(users.map((u) => [u.id, u.full_name]))} />
      )}
      {enabled.includes("created_by") && (
        <FBSelect label="Created by" testId="filter-created-by"
          value={value.created_by} onChange={set("created_by")}
          options={users.map((u) => u.id)}
          optionLabels={Object.fromEntries(users.map((u) => [u.id, u.full_name]))} />
      )}

      {enabled.includes("date_range") && (
        <div className="flex items-center gap-1 border border-border rounded-sm px-2 py-1 text-sm">
          <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="date"
            value={value.date_from || ""}
            onChange={setText("date_from")}
            data-testid="filter-date-from"
            className="bg-transparent text-xs outline-none"
          />
          <span className="text-muted-foreground text-xs">→</span>
          <input
            type="date"
            value={value.date_to || ""}
            onChange={setText("date_to")}
            data-testid="filter-date-to"
            className="bg-transparent text-xs outline-none"
          />
        </div>
      )}

      {has && (
        <button
          onClick={() => onChange({})}
          data-testid="filter-clear"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1.5"
        >
          <X className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  );
}

function FBSelect({ label, value, onChange, options, optionLabels = {}, testId }) {
  return (
    <Select value={value || ANY} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-[160px] h-9 text-sm">
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
          <SelectValue placeholder={label} />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>All {label.toLowerCase()}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{optionLabels[o] || o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
